import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'motion/react';
import { RefreshCw, Home, Mail, AlertTriangle, ChevronDown, ChevronUp } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export interface ServerErrorProps {
    error?: Error | null;
    resetError?: () => void;
}

export const ServerError: React.FC<ServerErrorProps> = ({ error, resetError }) => {
    const { t } = useTranslation();
    const [showDetails, setShowDetails] = useState(false);

    useEffect(() => {
        document.title = `500 - ${t('errors.500.badge', 'Serverfehler')} | Mosaik e.V.`;
    }, [t]);

    const handleRetry = () => {
        if (resetError) {
            resetError();
        } else {
            window.location.reload();
        }
    };

    return (
        <div className="relative min-h-[75vh] flex items-center justify-center py-16 px-4 sm:px-6 lg:px-8 overflow-hidden">
            {/* Ambient Lighting / Glow Background */}
            <div
                aria-hidden="true"
                className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl pointer-events-none"
            />
            <div
                aria-hidden="true"
                className="absolute bottom-1/4 right-1/3 w-80 h-80 bg-accent/10 rounded-full blur-3xl pointer-events-none"
            />

            <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, ease: 'easeOut' }}
                className="relative z-10 max-w-2xl w-full text-center"
            >
                {/* Status Badge */}
                <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-semibold bg-amber-500/15 text-amber-700 dark:text-amber-400 border border-amber-500/30 mb-6">
                    <AlertTriangle className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400" />
                    {t('errors.500.badge', '500 - Serverfehler')}
                </div>

                {/* Big 500 Headline */}
                <h1 className="text-7xl sm:text-9xl font-extrabold tracking-tight bg-gradient-to-r from-amber-500 via-rose-500 to-accent bg-clip-text text-transparent select-none drop-shadow-sm">
                    500
                </h1>

                {/* Title & Description */}
                <h2 className="mt-4 text-2xl sm:text-3xl font-bold text-foreground">
                    {t('errors.500.title', 'Ein unerwarteter Fehler ist aufgetreten.')}
                </h2>
                <p className="mt-3 text-base sm:text-lg text-foreground/70 max-w-lg mx-auto leading-relaxed">
                    {t(
                        'errors.500.description',
                        'Auf unserem Server ist ein Problem aufgetreten. Bitte laden Sie die Seite neu oder versuchen Sie es in wenigen Augenblicken erneut.'
                    )}
                </p>

                {/* Action Buttons */}
                <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
                    <button
                        type="button"
                        onClick={handleRetry}
                        className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-primary text-primary-foreground font-medium shadow-md shadow-primary/25 hover:bg-primary/90 hover:shadow-lg transition-all duration-200 cursor-pointer"
                    >
                        <RefreshCw className="w-4 h-4" />
                        <span>{t('errors.500.retry', 'Seite neu laden')}</span>
                    </button>

                    <Link
                        to="/"
                        className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-secondary text-secondary-foreground font-medium border border-border hover:bg-slate-200/70 dark:hover:bg-slate-800 transition-colors duration-200"
                    >
                        <Home className="w-4 h-4" />
                        <span>{t('errors.500.back_home', 'Zur Startseite')}</span>
                    </Link>

                    <Link
                        to="/kontakt"
                        className="inline-flex items-center gap-2 px-4 py-3 rounded-xl text-foreground/70 hover:text-primary transition-colors text-sm font-medium"
                    >
                        <Mail className="w-4 h-4" />
                        <span>{t('errors.500.contact_support', 'Support kontaktieren')}</span>
                    </Link>
                </div>

                {/* Development / Debug Error View */}
                {error && import.meta.env.DEV && (
                    <div className="mt-10 text-left max-w-xl mx-auto">
                        <button
                            type="button"
                            onClick={() => setShowDetails(!showDetails)}
                            className="flex items-center justify-between w-full px-4 py-2 rounded-lg bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-900/60 text-xs font-mono text-amber-900 dark:text-amber-200 cursor-pointer"
                        >
                            <span>Fehlerdetails (Nur Entwicklungsmodus)</span>
                            {showDetails ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </button>
                        {showDetails && (
                            <pre className="mt-2 p-4 rounded-lg bg-slate-900 text-slate-100 text-xs font-mono overflow-x-auto max-h-60 border border-slate-800">
                                {error.name}: {error.message}
                                {error.stack && `\n\n${error.stack}`}
                            </pre>
                        )}
                    </div>
                )}
            </motion.div>
        </div>
    );
};

export default ServerError;
